import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../PageLayout/Footer';

const PROFILE_ENDPOINT = 'https://backproject.meanc.net/meanc/users/me/profile-show/';
const REFRESH_ENDPOINT = 'https://backproject.meanc.net/meanc/auth/token/refresh/';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState({ type: 'loading', message: 'Syncing profile…' });
  const [isRefreshing, setIsRefreshing] = useState(false);

  const getStoredAccess = () => localStorage.getItem('meanc_access');
  const getStoredRefresh = () => localStorage.getItem('meanc_refresh');

  const clearSession = useCallback(() => {
    localStorage.removeItem('meanc_access');
    localStorage.removeItem('meanc_refresh');
    setProfile(null);
  }, []);

  const handleLogout = useCallback(() => {
    clearSession();
    navigate('/access-lab');
  }, [clearSession, navigate]);

  const refreshAccessToken = useCallback(async () => {
    const refreshToken = getStoredRefresh();
    if (!refreshToken) {
      setStatus({ type: 'error', message: 'Your session expired. Sign in again.' });
      clearSession();
      return null;
    }

    setIsRefreshing(true);
    try {
      const response = await fetch(REFRESH_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.access) {
        throw new Error(data.detail || data.error || 'Unable to refresh session.');
      }

      localStorage.setItem('meanc_access', data.access);
      return data.access;
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
      clearSession();
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, [clearSession]);

  const fetchProfile = useCallback(
    async (tokenOverride, { attemptedRefresh = false } = {}) => {
      const accessToken = tokenOverride || getStoredAccess();
      if (!accessToken) {
        setStatus({ type: 'error', message: 'No access token found. Use the Access Lab to sign in.' });
        return;
      }

      try {
        const response = await fetch(PROFILE_ENDPOINT, {
          headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        });
        const data = await response.json().catch(() => ({}));

        if (response.status === 401 && !attemptedRefresh) {
          const refreshedToken = await refreshAccessToken();
          if (refreshedToken) {
            await fetchProfile(refreshedToken, { attemptedRefresh: true });
            return;
          }
        }

        if (!response.ok) {
          throw new Error(data.detail || data.error || 'Unable to load profile.');
        }

        setProfile(data);
        setStatus({ type: 'success', message: 'Profile synced from secure auth.' });
      } catch (error) {
        setStatus({ type: 'error', message: error.message });
      }
    },
    [refreshAccessToken]
  );

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleManualRefresh = useCallback(async () => {
    const refreshedToken = await refreshAccessToken();
    if (refreshedToken) {
      fetchProfile(refreshedToken, { attemptedRefresh: true });
    }
  }, [fetchProfile, refreshAccessToken]);

  const profileFields = useMemo(() => {
    if (!profile) return [];
    return [
      { label: 'Full name', value: [profile.first_name, profile.last_name].filter(Boolean).join(' ') },
      { label: 'Email', value: profile.email },
      { label: 'User type', value: profile.user_type || profile.role },
      { label: 'Country', value: profile.country },
      { label: 'Birthday', value: profile.date_of_birth },
    ].filter((item) => item.value);
  }, [profile]);

  const initials = useMemo(() => {
    if (!profile) return 'ME';
    const primary = profile.first_name || profile.username || profile.email || 'M';
    const secondary = profile.last_name || '';
    return `${primary.charAt(0)}${secondary.charAt(0)}`.toUpperCase();
  }, [profile]);

  return (
    <div className="profile-page">
      <section className="profile-page__hero">
        <div>
          <p className="profile-page__eyebrow">Secure Auth</p>
          <h1>Account profile</h1>
          <p className={`profile-page__status-text ${status.type}`}>
            {status.message}
          </p>
          <div className="profile-page__actions">
            <button type="button" onClick={() => fetchProfile()} disabled={status.type === 'loading'}>
              Refresh profile
            </button>
            <button type="button" onClick={handleManualRefresh} disabled={isRefreshing} className="is-ghost">
              {isRefreshing ? 'Refreshing…' : 'Refresh token'}
            </button>
            <button type="button" onClick={handleLogout} className="is-danger">
              Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="profile-page__content">
        {profile ? (
          <div className="profile-card">
            <header className="profile-card__header">
              {profile.profile_picture ? (
                <img src={profile.profile_picture} alt="Profile" />
              ) : (
                <span className="profile-card__avatar">{initials}</span>
              )}
              <div>
                <p className="profile-card__label">Authenticated user</p>
                <h2>{profile.first_name || profile.username || profile.email}</h2>
                <p className="profile-card__sub">ID #{profile.id}</p>
              </div>
            </header>
            <div className="profile-card__grid">
              {profileFields.map((field) => (
                <div key={field.label} className="profile-card__field">
                  <span>{field.label}</span>
                  <strong>{field.value}</strong>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="profile-page__empty">
            <p>We could not load your profile yet. Use the Access Lab to sign in and try again.</p>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default ProfilePage;
